import React, { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import ErrorMessage from "../../../components/text/ErrorMessage";
import RadioSelect from "../../../components/radio-select";
import { Customer, Designation } from "../../../core/api/models";
import api from "../../../api";
import PhoneInput from "../../../components/phone-input";
import countryCodes from "../../../utils/countryCodes.json";

import { ValidateEmail, ValidatePhoneNumberRegEx } from "../../../utils/Validatation";
import { DropdownList } from "react-widgets";
import { useDataLoader } from "react-remote-data-hooks";
import { AppPermissions, State } from "../../../core";
import { useSelector } from "react-redux";

interface FormProps {
  query: string;
}

const SearchCustomer: React.FunctionComponent<{
  error?: string;
  onFetchDetails: (data: Customer, query: string, searchType: string) => void;
  setStage: (stage: string) => void;
  onCloseSetStage?: () => void;
  onCancel?: () => void;
  handleContactlessCustomer?: (val: string) => void;
  hasContact?: boolean;
}> = (props) => {
  const wrapperRef = React.useRef<HTMLInputElement>(null);
  const authState = useSelector((state: State) => state.authUser);
  const isCreateCustomerWithoutContacts =
    authState.apiPermissions?.[AppPermissions.CreateCustomerWithoutContacts];
  const [searchType, setSearchType] = useState<string>("mobile");
  const [isFetching, setFetching] = useState<boolean>(false);
  const { control, handleSubmit, errors, watch, setValue, trigger } = useForm<FormProps>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });

  const radioOptions = useMemo(() => {
    const allOptions = [
      { label: "Mobile number", value: "mobile" },
      { label: "Email address", value: "email" },
      { label: "Designation", value: "designation" },
    ];

    const showDesignationOption =
      props?.handleContactlessCustomer && isCreateCustomerWithoutContacts;

    return allOptions.filter((option) => option.value !== "designation" || showDesignationOption);
  }, [props?.handleContactlessCustomer, isCreateCustomerWithoutContacts]);

  const { data: designations } = useDataLoader<Designation[]>(() =>
    api.designation.loadAllDesignation()
  );

  const onSubmit = async (data: { query: string }) => {
    if (!!props?.handleContactlessCustomer && searchType === "designation") {
      props.handleContactlessCustomer(data.query);
      return;
    }
    setFetching(true);
    const response = await api.customer.findCustomer(
      data.query,
      searchType === "civilId" ? "civilId" : "mobile"
    );
    props.onFetchDetails(response, data.query, searchType);
    setFetching(false);
  };

  const fieldName =
    searchType === "mobile" ? "Mobile number" : searchType === "email" ? "Email address" : "";

  const resetSearchQuery = (type: string) => {
    if (type === "mobile") {
      setValue("query", "91");
    } else {
      setValue("query", "");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="p-4">
        <div className="form-group font-weight-semibold mb-4 ">
          <div className="mb-2 text-uppercase search-lead">
            <RadioSelect
              defaultValue={searchType}
              options={radioOptions}
              optionLabel={"label"}
              optionValue={"value"}
              onChange={(val) => {
                setSearchType(val?.toString());
                resetSearchQuery(val?.toString());

                if (wrapperRef && wrapperRef.current) {
                  wrapperRef?.current?.focus?.();
                }
              }}
              paddingClass="pr-0"
            />
          </div>
          {searchType === "mobile" && (
            <Controller
              control={control}
              rules={{
                required: {
                  value: true,
                  message: "Mobile number is required",
                },
                validate: (value) => {
                  if (countryCodes?.some((item) => item?.dial_code === `+${value}`)) {
                    return "Mobile number is required";
                  } else {
                    const hasError = ValidatePhoneNumberRegEx(value.trim());
                    if (!hasError) return "Invalid Mobile number";
                  }
                },
              }}
              name="query"
              defaultValue="91"
              render={({ onChange, value, name }) => (
                <PhoneInput
                  placeholder="MOBILE NUMBER"
                  value={value}
                  onChange={(val) => {
                    onChange(val);
                    trigger("query");
                  }}
                  inputProps={{ autoFocus: true }}
                />
              )}
            />
          )}

          {searchType === "email" && (
            <Controller
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  return ValidateEmail(value.trim());
                },
              }}
              name="query"
              render={({ onChange, value, name }) => (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  placeholder="EMAIL ADDRESS"
                  name={name}
                  value={value}
                  id="query"
                  onChange={onChange}
                  autoFocus
                  ref={wrapperRef}
                />
              )}
            />
          )}

          {searchType === "designation" && (
            <Controller
              control={control}
              rules={{ required: true }}
              name="query"
              render={({ onChange, value, name }) => (
                <DropdownList
                  value={value}
                  filter="contains"
                  defaultValue={value}
                  data={designations?.map((key) => {
                    return {
                      _id: key._id,
                      name: key.name,
                    };
                  })}
                  textField={"name"}
                  valueField={"_id"}
                  placeholder={"Select Designation"}
                  onChange={(val) => {
                    onChange(val._id);
                  }}
                />
              )}
            />
          )}

          {errors.query && errors?.query?.message && searchType === "mobile" && (
            <ErrorMessage>{errors.query.message}</ErrorMessage>
          )}

          {errors.query && errors.query.type === "required" && searchType !== "mobile" && (
            <ErrorMessage>{fieldName} is required</ErrorMessage>
          )}

          {errors.query && errors.query.type === "validate" && searchType !== "mobile" && (
            <ErrorMessage>Invalid {fieldName}</ErrorMessage>
          )}
        </div>
        <div className="mb-2 text-right">
          {props.hasContact && (
            <button
              className="btn btn-outline-dark font-normal font-weight-bold mr-3"
              disabled={!watch("query") || isFetching}
              type="button"
              onClick={props.onCancel}
            >
              Cancel
            </button>
          )}
          <button
            className="btn btn-primary font-normal font-weight-bold"
            disabled={!watch("query") || isFetching}
          >
            Next
          </button>
        </div>
      </div>
    </form>
  );
};

export default SearchCustomer;
